import React, {ReactNode, useEffect, useMemo, useRef, useState} from 'react'
import {Box, useMultiStyleConfig, useMediaQuery} from '@chakra-ui/react'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Pagination, Mousewheel, Keyboard, EffectCreative} from 'swiper/modules'
import {useHeaderSize, useViewport, useAnalytics, useHeaderSizeWithoutBanner} from '_customHooks'
import {LiveText} from '_uiComponents'
// import {useHeaderDispatch} from '_context'

/**
 * A splittable module item carries both the desktop render (single slide)
 * and the mobile split (two separate slides, one per DoubleSame half).
 */
interface SplittableModuleItem {
    desktop: ReactNode
    splitSlides: ReactNode[]
}

type ModuleItem = ReactNode | SplittableModuleItem

interface AnimatedSlideProps {
    data: {
        title_animation_type: 'no-animation' | 'fixed' | 'size' | 'attached' | 'fade' | 'carousel'
        module_attached?: boolean
        svg?: {url: string; metadata: {height: number; width: number}}
        title?: string
        cta_text?: string
        cta_url?: string
        cta2_text?: string
        cta2_url?: string
        cta_inline_d?: boolean
        cta_inline_m?: boolean
        content_color?: string
        content_align?: 'left' | 'center' | 'right'
        class_name?: string
        custom_css?: string
        /** WCAG 1.4.5 – Live text equivalent for burned-in text in the module image/SVG */
        accessibility_image_livetext?: string
    }
    moduleItems: Array<ModuleItem>
}

/**
 * The page can be in one of two scroll ownership states:
 * - swiper: the vertical carousel owns the scroll and the document body is locked.
 * - footer: the regular document/footer owns the scroll and Swiper mousewheel handling is disabled.
 */
type ScrollMode = 'swiper' | 'footer'

/** Touch threshold used to detect a deliberate vertical swipe on touch devices. */
const SWIPE_THRESHOLD = 50

/**
 * Lower touch threshold used to release the footer from the last slide ONLY when a dynamic bottom
 * bar (iOS 26-style Liquid Glass) is present. With the bar the viewport shifts vertically during the
 * gesture, so the standard `SWIPE_THRESHOLD` (tuned to avoid jitter on desktop/stable viewports)
 * ends up requiring an impractically large swipe on real devices. This makes the release reachable
 * without changing the desktop / no-bar behavior.
 */
const FOOTER_RELEASE_THRESHOLD_DYNAMIC_BAR = 24

/** Accumulated wheel delta required to release the footer from the last slide. */
const WHEEL_FOOTER_RELEASE_THRESHOLD = 90

/** Time window used to reset the wheel release intent if wheel events stop arriving. */
const WHEEL_RELEASE_RESET_DELAY = 180

/**
 * Minimum gap (px) between the large viewport (100lvh) and the small viewport (100svh)
 * for us to consider a collapsing/expanding bottom browser bar to be present.
 * A few px of noise/rounding must not count; a real dynamic bar is tens of px (≈40px on iOS 26).
 */
const DYNAMIC_BOTTOM_BAR_MIN_GAP = 16

/**
 * Detects the iOS 26+ Safari "Liquid Glass" translucent bottom bar — the only case where the
 * navigation tweaks below (100svh height, lighter footer-release threshold) should kick in.
 *
 * A large `100lvh − 100svh` gap alone is NOT sufficient: older iOS (e.g. iPhone X / iOS 16) also has
 * a collapsing toolbar with a big gap, but it is OPAQUE and behaves like the historical viewport, so
 * it must keep the original behavior. We therefore require three conditions together:
 *  1. WebKit/Safari;
 *  2. a dynamic (collapsing) bottom bar → large lvh/svh gap;
 *  3. Safari 26+, inferred via a capability shipped in Safari 26 (CSS anchor positioning), because
 *     the iOS 26 user-agent is frozen and cannot be parsed reliably.
 *
 * Must stay in sync with the equivalent check in `_app` that toggles the `.has-dynamic-bottom-bar`
 * class for the Safari bar shim.
 */
const hasDynamicBottomBar = (): boolean => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return false

    const measure = (unit: string): number => {
        const probe = document.createElement('div')
        probe.style.cssText = `position:fixed;top:0;left:0;height:${unit};width:0;visibility:hidden;pointer-events:none`
        document.body.appendChild(probe)
        const height = probe.getBoundingClientRect().height
        probe.remove()
        return height
    }

    const isWebKit = typeof CSS !== 'undefined' && CSS.supports('-webkit-touch-callout', 'none')
    const isSafari26Plus = typeof CSS !== 'undefined' && CSS.supports('anchor-name', '--x')
    const hasCollapsingBar = measure('100lvh') - measure('100svh') > DYNAMIC_BOTTOM_BAR_MIN_GAP

    return isWebKit && isSafari26Plus && hasCollapsingBar
}
interface AnimatedSlideSwiper {
    slides: ArrayLike<unknown>
    realIndex: number
    slideNext: () => void
    slidePrev: (...args: Array<unknown>) => unknown
    animating?: boolean
    mousewheel?: {
        enable: () => void
        disable: () => void
    }
    pagination?: {
        el?: HTMLElement
    }
}

const DESKTOP_OVERLAP_EFFECT = {
    limitProgress: 2,
    prev: {
        translate: [0, '-24%', 0],
        opacity: 0.92,
    },
    next: {
        translate: [0, '100%', 0],
    },
}

const AnimatedSlide: React.FC<AnimatedSlideProps> = ({data, moduleItems}) => {
    const {title_animation_type} = data
    const {isDesktop, breakpoint} = useViewport()
    const {headerSize, accessibilityBannerSize} = useHeaderSize(true)
    const {headerSize: headerSizeNotCollapsed} = useHeaderSize(false)
    const headerSizeCollapsedWithoutBanner = useHeaderSizeWithoutBanner(true)
    const [isWide] = useMediaQuery('(min-aspect-ratio: 20/11.25)')
    const [isUltrawide] = useMediaQuery('(min-aspect-ratio: 21/10)')
    const {analyticsEventCustom} = useAnalytics()

    /**
     * Resolve module items based on viewport:
     * On mobile/tablet, splittable items (DoubleSame) are expanded into individual slides.
     * On desktop, the desktop version is used as a single slide.
     */
    const isSplittableItem = (item: ModuleItem): item is SplittableModuleItem => {
        return item !== null && typeof item === 'object' && 'splitSlides' in (item as object)
    }

    const resolvedSlides: ReactNode[] = useMemo(() => {
        return moduleItems.flatMap((item) => {
            if (isSplittableItem(item)) {
                return isDesktop ? [item.desktop] : item.splitSlides
            }
            return [item as ReactNode]
        })
    }, [moduleItems, isDesktop])

    const useDesktopOverlapEffect = resolvedSlides.length > 1
    const containerRef = useRef<HTMLDivElement>(null)
    const prevSlideIndex = useRef<number>(0)
    const swiperRef = useRef<AnimatedSlideSwiper | null>(null)

    /** Single source of truth for scroll ownership. */
    const scrollModeRef = useRef<ScrollMode>('swiper')

    /** Accumulator for wheel-based release intent while the user is on the last slide. */
    const wheelFooterDeltaRef = useRef(0)
    const wheelReleaseResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    /** Touch gesture refs. These avoid re-rendering while we track gesture state. */
    const touchStartYRef = useRef(0)
    const touchStartXRef = useRef(0)
    const touchEdgeStartYRef = useRef<number | null>(null)
    const touchTriggeredRef = useRef(false)

    /**
     * Whether a dynamic bottom browser bar (iOS 26-style Liquid Glass and similar) is present.
     * Feature-detected at mount. When true we:
     *  - soften the last-slide → footer touch release (no "extra force" against the shifting viewport),
     *  - pin the mobile slide height to 100svh (stable, non-collapsing) via the theme.
     * On desktop/tablet and browsers without such a bar this stays false and the original
     * behavior (100vh height, edge-anchored release threshold) is preserved.
     *
     * A ref mirrors the state so the non-React touch handlers read it without stale closures.
     */
    const [dynamicBottomBar, setDynamicBottomBar] = useState(false)
    const hasDynamicBottomBarRef = useRef(false)
    const styles = useMultiStyleConfig('AnimatedSlide', {
        headerSize,
        headerSizeNotCollapsed,
        headerSizeCollapsedWithoutBanner,
        accessibilityBannerSize,
        isWide,
        isUltrawide,
        hasDynamicBottomBar: dynamicBottomBar,
    }) as {container: Record<string, unknown>; moduleItemWrapper: Record<string, unknown>}

    const getLastSlideIndex = (swiper: AnimatedSlideSwiper) => swiper.slides.length - 1
    const isLastSlide = (swiper: AnimatedSlideSwiper) => swiper.realIndex === getLastSlideIndex(swiper)
    const isFirstSlide = (swiper: AnimatedSlideSwiper) => swiper.realIndex === 0
    const isSwiperMode = () => scrollModeRef.current === 'swiper'
    const isFooterMode = () => scrollModeRef.current === 'footer'

    const syncHeaderScrollState = (swiper: AnimatedSlideSwiper) => {
        window.dispatchEvent(
            new CustomEvent('animated-slide-state-change', {
                detail: {
                    active: true,
                    isFirstSlide: isFirstSlide(swiper),
                    scrollOwner: scrollModeRef.current,
                },
            }),
        )
    }

    const handlePaginationClick = (event: Event) => {
        const bullet = (event.target as HTMLElement).closest('.swiper-pagination-bullet')
        if (!bullet) return

        analyticsEventCustom({
            category: 'scroll_block',
            action: 'click',
            label: 'click',
        })
    }

    const resetFooterWheelIntent = () => {
        wheelFooterDeltaRef.current = 0

        if (wheelReleaseResetTimerRef.current) {
            clearTimeout(wheelReleaseResetTimerRef.current)
            wheelReleaseResetTimerRef.current = null
        }
    }

    /**
     * Centralized mode setter.
     * Keeping body overflow and Swiper mousewheel state here avoids scattered side effects.
     */
    const setScrollMode = (mode: ScrollMode) => {
        scrollModeRef.current = mode

        if (mode === 'swiper') {
            resetFooterWheelIntent()
            swiperRef.current?.mousewheel?.enable()
            document.body.style.overflow = 'hidden'
            return
        }

        swiperRef.current?.mousewheel?.disable()
        document.body.style.overflow = ''
    }

    const lockPageScroll = () => {
        setScrollMode('swiper')
    }

    const getActiveSlideElement = () => {
        return containerRef.current?.querySelector(':scope > .swiper > .swiper-wrapper > .swiper-slide-active') as HTMLElement | null
    }

    const isScrollableElementAtBottom = (element: HTMLElement) => {
        // With a dynamic bottom bar (iOS 26-style Liquid Glass) `clientHeight` oscillates as the bar
        // collapses/expands mid-scroll, so a tight 1px tolerance makes "at bottom" flip-flop and the
        // footer release fires only intermittently ("only rarely reaches the footer"). A larger
        // tolerance absorbs that jitter. Without the bar we keep the strict 1px check.
        const bottomTolerance = hasDynamicBottomBarRef.current ? 48 : 1
        return Math.ceil(element.scrollTop) + element.clientHeight >= element.scrollHeight - bottomTolerance
    }

    const isScrollableElementAtTop = (element: HTMLElement) => {
        return element.scrollTop <= 0
    }

    const isElementScrollable = (element: HTMLElement) => {
        return element.scrollHeight > element.clientHeight + 1
    }

    /**
     * Nested horizontal Swipers must keep ownership of horizontal gestures.
     * This prevents the vertical carousel from stealing horizontal swipe/wheel interactions.
     */
    const isHorizontalNestedSwiperInteraction = (eventTarget: EventTarget | null, deltaX = 0, deltaY = 0) => {
        if (!(eventTarget instanceof HTMLElement)) return false

        const nestedHorizontalSwiper = eventTarget.closest('.swiper-horizontal')
        if (!nestedHorizontalSwiper) return false

        return Math.abs(deltaX) > Math.abs(deltaY)
    }

    /**
     * Shared check used by wheel and touch flows.
     * Release is allowed only from the last slide, after the Swiper transition
     * has completed and the active slide cannot consume more downward scroll.
     */
    const canActiveSlideReleaseFooter = (swiper: AnimatedSlideSwiper, activeSlide: HTMLElement) => {
        if (!isLastSlide(swiper) || swiper.animating) return false

        const slideScrollable = isElementScrollable(activeSlide)
        return !slideScrollable || isScrollableElementAtBottom(activeSlide)
    }

    const releasePageScrollToFooter = (swiper: AnimatedSlideSwiper, scrollToFooter = false) => {
        if (!isSwiperMode()) return

        scrollModeRef.current = 'footer'
        syncHeaderScrollState(swiper)
        swiper.mousewheel?.disable()
        document.body.style.overflow = ''
        resetFooterWheelIntent()

        // Touch only: the gesture that triggers the release is consumed (we called preventDefault on
        // its frames and stop tracking it), so without a nudge the page stays pinned at the top of the
        // swiper and the user has to perform a SECOND upward gesture to actually see the footer. Jump
        // the window to just below the swiper so the footer is revealed within the same gesture.
        if (scrollToFooter && containerRef.current) {
            const swiperBottom = containerRef.current.getBoundingClientRect().bottom + window.scrollY
            requestAnimationFrame(() => {
                window.scrollTo({top: Math.max(0, Math.round(swiperBottom)), behavior: 'auto'})
            })
        }
    }

    /**
     * Accumulates wheel energy while on the last slide.
     * This avoids releasing the footer on tiny accidental wheel deltas, but keeps the behavior trackpad-friendly.
     */
    const tryReleaseFooterFromWheel = (deltaY: number, swiper: AnimatedSlideSwiper, activeSlide: HTMLElement) => {
        if (!canActiveSlideReleaseFooter(swiper, activeSlide)) {
            resetFooterWheelIntent()
            return false
        }

        wheelFooterDeltaRef.current += deltaY

        if (wheelReleaseResetTimerRef.current) {
            clearTimeout(wheelReleaseResetTimerRef.current)
        }

        wheelReleaseResetTimerRef.current = setTimeout(() => {
            resetFooterWheelIntent()
        }, WHEEL_RELEASE_RESET_DELAY)

        if (wheelFooterDeltaRef.current >= WHEEL_FOOTER_RELEASE_THRESHOLD) {
            releasePageScrollToFooter(swiper)
            return true
        }

        return false
    }

    /**
     * Slide-change side effects are kept small:
     * - Entering the last slide records the timestamp used by footer release logic.
     * - Leaving the last slide restores carousel-owned scroll.
     * - The window is kept aligned with the carousel area while navigating slides.
     */
    const handleSlideChange = (swiper: AnimatedSlideSwiper): void => {
        if (!isLastSlide(swiper)) {
            lockPageScroll()
        } else {
            resetFooterWheelIntent()
        }

        if (!isFirstSlide(swiper)) {
            containerRef.current?.classList.add('not-on-first-slide')
        } else {
            containerRef.current?.classList.remove('not-on-first-slide')
        }

        syncHeaderScrollState(swiper)
        prevSlideIndex.current = swiper.realIndex
    }

    const activateReverseTransition = () => {
        containerRef.current?.classList.add('is-reverse-transition')
    }

    const handleSlideChangeTranstionEnd = (): void => {
        containerRef.current?.classList.remove('is-reverse-transition')
    }

    const calculateSpaceBetweenSlides = () => {
        if (!breakpoint) {
            return 0
        }

        if (!['xl', '2xl'].includes(breakpoint)) {
            return 0
        }

        return (headerSize[breakpoint] - (accessibilityBannerSize[breakpoint] * 2 || 0)) * -1
    }

    /**
     * Touch fallback for devices where the Swiper mousewheel module is not involved.
     * It allows native scrolling inside a slide, changes slide only at vertical scroll boundaries,
     * and releases the footer only after an extra downward intent on the last slide.
     */
    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        const handleTouchStart = (event: TouchEvent) => {
            touchStartYRef.current = event.touches[0].clientY
            touchStartXRef.current = event.touches[0].clientX
            touchEdgeStartYRef.current = null
            touchTriggeredRef.current = false
        }

        const handleTouchMove = (event: TouchEvent) => {
            const swiper = swiperRef.current
            if (!swiper || swiper.animating || touchTriggeredRef.current || !isSwiperMode()) return

            const currentY = event.touches[0].clientY
            const currentX = event.touches[0].clientX
            const deltaY = touchStartYRef.current - currentY
            const deltaX = touchStartXRef.current - currentX

            if (isHorizontalNestedSwiperInteraction(event.target, deltaX, deltaY)) {
                return
            }

            const activeSlide = getActiveSlideElement()
            if (!activeSlide) return

            const scrollingDown = deltaY > 0
            const scrollingUp = deltaY < 0
            const slideScrollable = isElementScrollable(activeSlide)
            const activeSlideAtBottom = !slideScrollable || isScrollableElementAtBottom(activeSlide)
            const activeSlideAtTop = !slideScrollable || isScrollableElementAtTop(activeSlide)

            if (isLastSlide(swiper) && scrollingDown && activeSlideAtBottom) {
                // Suppress the browser's native scroll for the WHOLE gesture while we accumulate the
                // release intent, not just when the threshold is crossed. Otherwise every sub-threshold
                // frame falls through to `return` without preventDefault and the browser scrolls the
                // page/slide natively — which is exactly the reported symptom: the last slide "scrolls"
                // and the slide underneath peeks out, and the footer is only rarely reached. The
                // touchmove listener is registered {passive: false} specifically to allow this.
                event.preventDefault()

                // We only reach this branch once the active slide is already at its bottom
                // (`activeSlideAtBottom`), so any further downward movement is release intent.
                const hasDynamicBar = hasDynamicBottomBarRef.current

                if (touchEdgeStartYRef.current === null) {
                    // With a dynamic bottom bar (iOS 26-style Liquid Glass): anchor the release intent
                    // to the gesture origin so releasing the footer takes the same effort as a slide
                    // change — even when the slide is internally scrollable, because we are already at
                    // its bottom here. Without the bar (desktop/tablet, Android, "old" Safari): keep
                    // measuring from the frame the bottom was reached, which requires extra downward
                    // force after the edge and prevents jitter on the last-slide → footer boundary.
                    touchEdgeStartYRef.current = hasDynamicBar ? touchStartYRef.current : currentY
                }

                const edgeDelta = touchEdgeStartYRef.current - currentY

                // Lower, easier-to-reach threshold when the bar is present (the shifting viewport
                // otherwise makes the standard threshold require an impractically large swipe on
                // real devices); the original threshold everywhere else.
                const releaseThreshold = hasDynamicBar ? FOOTER_RELEASE_THRESHOLD_DYNAMIC_BAR : SWIPE_THRESHOLD

                if (edgeDelta > releaseThreshold) {
                    touchTriggeredRef.current = true
                    // Pass `true` so the release also nudges the window down to the footer within this
                    // same gesture (touch gestures are consumed on release; see releasePageScrollToFooter).
                    releasePageScrollToFooter(swiper, true)
                }

                return
            }

            if (slideScrollable && !activeSlideAtBottom && scrollingDown) {
                return
            }

            if (slideScrollable && !activeSlideAtTop && scrollingUp) {
                return
            }

            if (Math.abs(deltaY) <= SWIPE_THRESHOLD) {
                return
            }

            touchTriggeredRef.current = true
            event.preventDefault()

            if (scrollingDown && !isLastSlide(swiper)) {
                swiper.slideNext()
            } else if (scrollingUp && !isFirstSlide(swiper)) {
                swiper.slidePrev()
            }
        }

        container.addEventListener('touchstart', handleTouchStart, {passive: true})
        container.addEventListener('touchmove', handleTouchMove, {passive: false})

        return () => {
            container.removeEventListener('touchstart', handleTouchStart)
            container.removeEventListener('touchmove', handleTouchMove)
        }
    }, [])

    /**
     * Initial page setup.
     * The document starts locked so the first scroll gestures are owned by the vertical carousel.
     */
    useEffect(() => {
        const dynamicBar = hasDynamicBottomBar()
        hasDynamicBottomBarRef.current = dynamicBar
        setDynamicBottomBar(dynamicBar)

        window.scrollTo({top: 0, behavior: 'instant'})
        lockPageScroll()

        requestAnimationFrame(() => {
            window.scrollTo({top: 0, behavior: 'instant'})
        })

        return () => {
            document.body.style.overflow = ''
            resetFooterWheelIntent()
            scrollModeRef.current = 'swiper'

            // On unmount (e.g. React-router navigation away from the homepage) tell the header/banner
            // consumers the animated slide is gone. Otherwise `useScrollAtTop` keeps the last
            // dispatched `{active: true, isFirstSlide: false}` and the header stays "closed"
            // (opaque, with a white top border) on the next page until a real scroll happens.
            window.dispatchEvent(
                new CustomEvent('animated-slide-state-change', {
                    detail: {active: false, isFirstSlide: true, scrollOwner: 'footer'},
                }),
            )
        }
    }, [])

    /**
     * Re-entry from footer to carousel.
     * Semantic state is published before the body is locked, so the header does
     * not interpret the footer boundary as the real top of the first slide.
     */
    useEffect(() => {
        const handleWindowScroll = () => {
            const swiper = swiperRef.current
            if (!swiper || !isFooterMode() || !isLastSlide(swiper)) return

            if (window.scrollY <= 0) {
                syncHeaderScrollState(swiper)
                lockPageScroll()
            }
        }

        window.addEventListener('scroll', handleWindowScroll, {passive: true})

        return () => {
            window.removeEventListener('scroll', handleWindowScroll)
        }
    }, [])

    /**
     * Wheel release bridge for the last slide.
     * Swiper still handles normal wheel navigation between slides.
     * This listener only intercepts downward wheel events on the last slide after the active slide reached its bottom.
     */
    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        const handleFooterWheelRelease = (event: WheelEvent) => {
            const swiper = swiperRef.current
            if (!swiper || !isSwiperMode()) return

            const deltaY = event.deltaY
            const deltaX = event.deltaX
            const scrollingDown = deltaY > 0

            if (!scrollingDown || !isLastSlide(swiper)) {
                resetFooterWheelIntent()
                return
            }

            if (isHorizontalNestedSwiperInteraction(event.target, deltaX, deltaY)) {
                return
            }

            const activeSlide = getActiveSlideElement()
            if (!activeSlide) return

            const slideScrollable = isElementScrollable(activeSlide)
            const activeSlideAtBottom = !slideScrollable || isScrollableElementAtBottom(activeSlide)

            if (!activeSlideAtBottom) {
                resetFooterWheelIntent()
                return
            }

            const released = tryReleaseFooterFromWheel(deltaY, swiper, activeSlide)

            if (released) return

            event.preventDefault()
            event.stopPropagation()
        }

        container.addEventListener('wheel', handleFooterWheelRelease, {passive: false, capture: true})

        return () => {
            container.removeEventListener('wheel', handleFooterWheelRelease, {capture: true})
        }
    }, [])

    /**
     * managing scroll down button component scroll,
     * changing slide instead moving outside animation component
     */
    useEffect(() => {
        const handleScrollDown = () => {
            swiperRef.current?.slideNext()
        }

        window.addEventListener('animated-scroll-down', handleScrollDown)

        return () => {
            window.removeEventListener('animated-scroll-down', handleScrollDown)
        }
    }, [])

    const renderSwiper = () => (
        <Swiper
            spaceBetween={useDesktopOverlapEffect ? 0 : calculateSpaceBetweenSlides()}
            direction="vertical"
            allowTouchMove={false}
            watchSlidesProgress={useDesktopOverlapEffect}
            effect={useDesktopOverlapEffect ? 'creative' : 'slide'}
            creativeEffect={useDesktopOverlapEffect ? DESKTOP_OVERLAP_EFFECT : undefined}
            pagination={{
                enabled: isDesktop,
                clickable: true,
            }}
            mousewheel={{
                enabled: true,
                releaseOnEdges: false,
                thresholdDelta: 25,
                thresholdTime: 300,
                forceToAxis: true,
            }}
            keyboard
            speed={600}
            modules={[Pagination, Mousewheel, Keyboard, EffectCreative]}
            className="mySwiper cc-animated-slide-swiper"
            onSwiper={(swiper) => {
                const currentSwiper = swiper as AnimatedSlideSwiper
                const originalSlidePrev = currentSwiper.slidePrev.bind(currentSwiper)

                currentSwiper.slidePrev = (...args) => {
                    activateReverseTransition()
                    return originalSlidePrev(...args)
                }

                swiper.pagination?.el?.addEventListener('click', handlePaginationClick)

                swiperRef.current = currentSwiper
                syncHeaderScrollState(currentSwiper)
            }}
            onRealIndexChange={(swiper) => {
                handleSlideChange(swiper)
            }}
            onSlideChangeTransitionEnd={() => {
                handleSlideChangeTranstionEnd()
            }}
        >
            {resolvedSlides?.map((component, idx) => (
                <SwiperSlide key={idx}>
                    <Box {...styles.moduleItemWrapper} data-testid="animated-slide-module-wrapper">
                        {component}
                    </Box>
                </SwiperSlide>
            ))}
        </Swiper>
    )

    return (
        <>
            <Box className="cc-animated-slide" data-testid="animated-slide" sx={styles.container} ref={containerRef}>
                {title_animation_type === 'carousel' && renderSwiper()}
            </Box>
            {data.accessibility_image_livetext && <LiveText>{data.accessibility_image_livetext}</LiveText>}
        </>
    )
}

export {AnimatedSlide}
